import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useHistory, useParams, useLocation  } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { secureGet, securePost } from '../../config/api';
import { formatDateTime, formatAmount, formatDate } from '../../utils/format';
import Input from '../../components/Input';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';
import { EventMessage } from './EventMessage';
import MarkPaidPopup from '../../components/MarkPaidPopup';
import UserContext from '../../Shared/UserContext';
import AttachPopup from './AttachPopup';
import DeletePopup from "../../components/DeletePopup";
import ImagePopupViewer from "../../components/ImagePopupViewer";

import EditIcon from '../../icons/editB.svg';
import noImage from "../../icons/noImageIcon.svg";
import DeleteIcon from '../../icons/trashR.svg';
import UnpaidIcon from '../../icons/danger.svg';
import checkIcon from "../../icons/check.svg";
import plusIcon from '../../icons/plusP.svg';

const STATUS_COLORS = {
    CREATED: "#0283F4",
    UNDER_APPROVAL: "#FEA26F",
    VALIDATED: "#97DFFC",
    DELIVERED: "#06D6A0",
    DECLINED: "#EF466F",
    CANCELLED: "#A8ADC3",
};

const PaymentsDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('general_infos');
    const { t } = useTranslation();
    const { id } = useParams();
    const [payment, setPayment] = useState(null);
    const [, setLoading] = useState(true);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [attachPopupOpen, setAttachPopupOpen] = useState(false);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };
    const { state: { role } } = useContext(UserContext);

    const handleAttachSuccess = () => {
        fetchPayment();
        showSnackbar(t(`confirmation.paymentAttach`));
    };

    const abortController = useMemo(() => new AbortController(), []);
    const { signal } = abortController;

    const fetchPayment = useCallback(async () => {
        setLoading(true);
        try {
            const response = await secureGet(`/payments/${id}`, showSnackbar, { signal });
            const fetchedPayment = response.data;
            setPayment(fetchedPayment);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Failed to fetch payment:", error);
            }
        } finally {
            setLoading(false);
        }
    }, [id, signal]);

    useEffect(() => {
        fetchPayment();

        return () => {
            abortController.abort();
        };
    }, [id, abortController, fetchPayment]);

    const handleModifyClick = () => {
        history.push({
            pathname: `/payments/details/modify/${payment.id}`,
            state: { filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
        });
    };

    const detachBLConfirm = (bl) => {
        setItemToDelete(bl);
        setDeletePopupVisible(true);
    }

    const detachBL = async () => {
        if (!itemToDelete) return;
        setDeleting(true);
        const payload = {
            paymentId: payment.id,
            attachedAmount: itemToDelete.attachedAmount,
            deliveryNoteId: itemToDelete.deliveryNoteId,
        };
        try {
            const response = await securePost(`payments/detachCart`, payload, showSnackbar);
            setDeletePopupVisible(false);
            setItemToDelete(null);
            fetchPayment();

            showSnackbar(t(`confirmation.paymentDetach`));
        } catch (error) {
            console.error("Failed to detach BLs:", error);
        } finally {
            setDeleting(false);
            setDeletePopupVisible(false);
            setItemToDelete(null);
        }
    }

    const handleMarkPaid = async (reason) => {
        const payload = {
            id: payment.id,
            reason: reason,
        }

        try {
            const response = await securePost("/payments/unmarkImpaye", payload, showSnackbar);
            setPopupOpen(false);
            fetchPayment();
            showSnackbar(t(`confirmation.markPaid`));
        } catch (error) {
            console.error("Failed to mark as unpaid:", error);
        }
    }


    const handleMarkUnpaid = async () => {
        try {
            const response = await securePost(`/payments/markImpaye/${payment.id}`, {}, showSnackbar);
            fetchPayment();
            showSnackbar(t(`confirmation.markUnpaid`));

        } catch (error) {
            console.error("Failed to mark as unpaid:", error);
        }
    };

    const handleUpdateStatus = async () => {
        try {
            const response = await securePost(`/payments/updateValidated`, { paymentId: payment.id }, showSnackbar);
            fetchPayment();
            showSnackbar(t(`confirmation.paymentValidation`));
        }
        catch (error) {
            console.error("Failed to update payment status:", error);
        }
    }

    const renderActionButtons = () => {
        return (
            <>
                {role === 'ROLE_ADMIN' && payment.status === "UNDER_APPROVAL" && (
                    <button
                        className="flex items-center justify-center bg-[#0AD69F] text-[#FFFFFF] font-bold text-[14px] px-6 py-3 rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
                        onClick={() => handleUpdateStatus("VALIDATED")}
                    >
                        <img src={checkIcon} alt="" className="w-5 h-5" />
                        {t(`actions.validate`)}
                    </button>
                )}
                <button
                    className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
                    onClick={handleModifyClick}
                >
                    <img src={EditIcon} alt="modify" className="w-5 h-5" />
                    {t('actions.modify')}
                </button>
            </>
        );
    };

    const handleMarkUnpaidClick = () => {
        setPopupOpen(true);
    };

    const handleCancelGenerate = () => {
        setPopupOpen(false);
    };

    if (!payment) {
        return <LoadingLogo />;
    }


    return (
        <div>
            {/* Payment Details */}
            <Helmet>
                <title>GrayFox - {t("titles.payment_details")}</title>
            </Helmet>
            <div className="flex flex-col mb-[20px]">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <Breadcrumbs />
                        <h1 className="text-[32px] font-semibold">{t(`titles.payment_details`)}</h1>
                    </div>
                    <div className="ml-auto flex items-center space-x-4 text-[14px]">
                        {renderActionButtons()}
                    </div>
                </div>
                <div className="flex items-center space-x-2 ">
                    <p className="text-[24px] font-semibold text-primary">
                        {payment.reference}
                    </p>
                    <div >
                        <span
                            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full text-center"
                            style={{ backgroundColor: STATUS_COLORS[payment.status] || "#A8ADC3" }}
                        >
                            {t(`payment_status.${payment.status}`).toUpperCase()}
                        </span>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex bg-gray-200 rounded-[12px] p-1 w-fit">
                {['general_infos', 'attachments', 'history'].map((tabKey) => (
                    <button
                        key={tabKey}
                        className={`px-20 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === tabKey
                            ? 'bg-white text-[#043F88] shadow'
                            : 'text-[#ACAFC6]'}`}
                        onClick={() => setActiveTab(tabKey)}
                    >
                        {t(`titles.${tabKey}`).toUpperCase()}
                    </button>
                ))}
            </div>


            <div className="flex gap-4">

                {/* LEFT */}
                <div className="w-[75%]">
                    {activeTab === "general_infos" && (
                        <div className="flex gap-4 mt-6">
                            <div className="w-full">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                    <Input label={t(`paymentDetails.client`)} value={payment.client?.name} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.type`)} value={payment.type?.name} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.bank`)} value={payment.bank?.name} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.number`)} value={payment.reference} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.amount`)} name="amount" value={payment.amount} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.date_echeance`)} value={formatDate(payment.echeance)} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.date_facture`)} value={formatDate(payment.facture)} onChange={() => { }} disabled={true} />
                                    <Input label={t(`paymentDetails.comment`)} value={payment.note  || ''} onChange={() => { }} disabled={true} />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "attachments" && (
                        <>
                            <div className="mt-6">
                                <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-2">{t(`fields_types.details`)}</h3>
                                <div className="mt-2 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                                    {[
                                        { key: "amount", title: t(`paymentDetails.montant`).toUpperCase(), color: "#FEA26F" },
                                        { key: "totalAttachedAmount", title: t(`paymentDetails.montant_attached`).toUpperCase(), color: "#FFD166" },
                                        { key: "toAttach", title: t(`paymentDetails.montant_to_attach`).toUpperCase(), color: "#06D6A0" },
                                    ].map(({ key, title, color }, index, arr) => {
                                        const value =
                                            key === "toAttach"
                                                ? (payment[arr[0].key]) - (payment[arr[1].key])
                                                : payment[key];

                                        return (
                                            <div
                                                key={key}
                                                className="bg-white rounded-[12px] relative flex flex-col justify-center min-h-[90px]"
                                                style={{ boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)" }}
                                            >
                                                <div className="absolute left-0 h-[55px] w-[5px] rounded-r-md" style={{ backgroundColor: color }}></div>
                                                <h4 className="text-[#707070] text-[10px] font-semibold ml-6">{title}</h4>
                                                <p className="font-bold text-[24px] ml-6" style={{ color }}>
                                                    {formatAmount(value)}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>

                            <div className="mt-4">
                                <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center gap-1">
                                        <h3 className="text-[16px] font-medium text-[#ACAFC6]">
                                            {t(`fields_types.attached`)}
                                        </h3>
                                        <h3 className="text-[16px] text-secondary font-semibold">
                                            ({payment.deliveryNotePayments.length})
                                        </h3>
                                    </div>

                                    <div className="flex items-center ">
                                        <img src={plusIcon} alt="Add" className="w-4 h-4" />
                                        <button
                                            onClick={() => setAttachPopupOpen(true)}
                                            className="text-primary text-[14px] font-bold underline"
                                        >
                                            {t('actions.add').toUpperCase()}
                                        </button>
                                    </div>

                                </div>

                                {payment.deliveryNotePayments?.map((bl, index) => (
                                    <div
                                        key={index}
                                        className="flex justify-between items-center bg-white p-4 mb-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
                                    >
                                        {/* Date + Delivery Note Number */}
                                        <div>
                                            <p className="text-[12px] font-semibold text-secondary">
                                                {formatDateTime(bl.createdAt)}
                                            </p>
                                            <p className="text-[#020040] font-semibold text-[16px]">
                                                {bl.deliveryNoteNumber || bl.deliveryNoteId}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-20 mr-10">
                                            {/* Amount */}
                                            <p className="text-[#043F88] text-[18px] font-semibold">
                                                {formatAmount(bl.attachedAmount)}
                                            </p>

                                            {/* Delete Button */}
                                            <button
                                                type="button"
                                                onClick={() => detachBLConfirm(bl)}
                                                className="w-8 h-8 flex items-center justify-center bg-[#F2F3F3] border border-[#EF466F4D] rounded-[12px]"
                                            >
                                                <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}


                            </div>
                        </>
                    )}

                    {activeTab === "history" && (
                        <div className="flex gap-4 mt-6">
                            <div className="w-full">
                                <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">{t(`fields_types.operations`)}</h3>
                                {payment.events?.map((event, index) => (
                                    <div key={index}
                                        className="grid grid-cols-[100%] items-center bg-white p-4 mb-2 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">

                                        <div className="flex items-center space-x-2 w-full h-full ">
                                            <div>
                                                <p className="text-[12px] font-semibold text-secondary ">
                                                    {formatDateTime(event.date)}
                                                </p>
                                                <EventMessage event={event} />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                            </div>
                        </div>
                    )}
                </div>

                {/* image */}
                <div className="w-[25%] mt-4">
                    {(role !== 'ROLE_COMMERCIAL' && (payment.type?.name === 'Chèque' || payment.type?.name === 'Effet')) && (
                        <div className="flex justify-end mb-1">
                            <button
                                onClick={() => payment.impaye ? handleMarkUnpaidClick() : handleMarkUnpaid()}
                                className="text-[14px] font-semibold text-[#EF466F] flex items-center"
                            >
                                <img src={UnpaidIcon} alt="Unpaid" className="w-4 h-4 mr-2" />
                                {payment.impaye ? t(`actions.MarkasPaid`) : t(`actions.MarkasUnpaid`)}
                            </button>
                        </div>
                    )}

                    <div className="bg-white shadow rounded-[12px] p-[2px] relative">
                        <div className="bg-[#F5F6FB] rounded-[12px] overflow-hidden ">
                            <ImagePopupViewer src={payment.image} fallbackSrc={noImage} />
                        </div>
                    </div>
                </div>
            </div>
            {payment && (
                <AttachPopup
                    open={attachPopupOpen}
                    onClose={() => setAttachPopupOpen(false)}
                    payment={payment}
                    clientId={payment.client?.id}
                    onAttachSuccess={handleAttachSuccess}
                />
            )}

            {/* Delete Popup */}
            {isDeletePopupVisible && (
                <DeletePopup
                    open={isDeletePopupVisible}
                    onCancel={() => setDeletePopupVisible(false)}
                    onConfirm={detachBL}
                    loading={deleting}
                />
            )}

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />

            <MarkPaidPopup
                open={isPopupOpen}
                onConfirm={(reason) => handleMarkPaid(reason)}
                onCancel={handleCancelGenerate}
            />
        </div>
    );
};

export default PaymentsDetails;
